// Canonical set of UI locales the product ships translations for. Single
// source of truth for the TypeScript layers: the server request/response zod
// schemas, the shared view contracts, and the web app's `use-intl`
// `AppConfig['Locale']` augmentation all derive from this list.
//
// Adding a language: add its code here, drop a `apps/web/messages/<code>/`
// catalog folder, AND add a migration widening the `users.locale` CHECK
// constraint (its literal is mirrored in `identity/schema.ts`). SQL can't
// import this constant, so the DB CHECK is the one layer that must be updated
// by hand in lockstep — it does not pick the new code up automatically.
export const SUPPORTED_LOCALES = ['en', 'es', 'fr'];
export const DEFAULT_LOCALE = 'en';
// Endonyms — each language named in its own language, the accessibility
// convention for a language picker.
export const LOCALE_LABELS = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
};
export function isSupportedLocale(value) {
    return (typeof value === 'string' &&
        SUPPORTED_LOCALES.includes(value));
}
// The subset of SUPPORTED_LOCALES exposed to users right now — consulted by
// both browser auto-detect (`detectLocale`) and the Settings language picker.
// All three ship: es/fr member surfaces are translated, and any key a locale
// hasn't caught up on falls back to English at render (see `messages/load.ts`),
// so exposing them never surfaces a raw key. A language graduates here only
// once it's safe to render this way; narrow the list if a future locale is
// added before its catalog is ready.
export const AVAILABLE_LOCALES = ['en', 'es', 'fr'];
export function isAvailableLocale(value) {
    return (typeof value === 'string' &&
        AVAILABLE_LOCALES.includes(value));
}
// Pick the best *available* locale from a browser language signal — either an
// HTTP `Accept-Language` header ("fr-CA,fr;q=0.9,en;q=0.8") or a joined
// `navigator.languages` list. Honours q-weights, matches on the base
// subtag, and only ever returns a locale in AVAILABLE_LOCALES (falling back
// to DEFAULT_LOCALE), so it can never select a language we'd render broken.
export function detectLocale(acceptLanguage) {
    if (!acceptLanguage)
        return DEFAULT_LOCALE;
    const ranked = acceptLanguage
        .split(',')
        .map((part) => {
        const [tag, qPart] = part.trim().split(';q=');
        const base = (tag ?? '').trim().toLowerCase().split('-')[0] ?? '';
        const weight = qPart === undefined ? 1 : Number(qPart);
        return { base, weight: Number.isFinite(weight) ? weight : 0 };
    })
        .filter((t) => t.base.length > 0)
        .sort((a, b) => b.weight - a.weight);
    for (const { base } of ranked) {
        if (isAvailableLocale(base))
            return base;
    }
    return DEFAULT_LOCALE;
}
// Narrow an untrusted / nullable string (a DB column, a browser
// `Accept-Language` tag, a stale cache) to a supported locale, falling back
// to the default. The one place a locale is allowed to "not be set".
export function resolveLocale(value) {
    return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}
//# sourceMappingURL=locale.js.map